import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
type ToastVariant = 'success' | 'error' | 'info';
type ToastItem = {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
};
type ToastContextValue = {
  showToast: (toast: Omit<ToastItem, 'id'>) => void;
};
const ToastContext = createContext<ToastContextValue | null>(null);
const variantStyles: Record<ToastVariant, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-950',
  error: 'border-red-200 bg-red-50 text-red-950',
  info: 'border-slate-200 bg-white text-slate-900'
};
const variantIcons = {
  success: CheckCircle2,
  error: XCircle,
  info: Info
};
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const dismissToast = useCallback((id: number) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);
  const showToast = useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = nextId.current++;
    setToasts(current => [...current, {
      ...toast,
      id
    }]);
    window.setTimeout(() => {
      setToasts(current => current.filter(item => item.id !== id));
    }, 3200);
  }, []);
  const value = useMemo(() => ({
    showToast
  }), [showToast]);
  return <ToastContext.Provider value={value} data-id="element-14">
      {children}
      <div className="pointer-events-none fixed top-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-3 px-4 sm:px-0" data-id="element-15">
        {toasts.map(toast => {
        const Icon = variantIcons[toast.variant];
        return <div key={toast.id} role="status" aria-live="polite" className={`pointer-events-auto rounded-xl border shadow-lg backdrop-blur ${variantStyles[toast.variant]}`} data-id="element-16">
            <div className="flex items-start gap-3 p-4" data-id="element-17">
              <div className="mt-0.5 shrink-0" data-id="element-18">
                <Icon className="h-5 w-5" data-id="element-19" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-20">
                <p className="text-sm font-semibold" data-id="element-21">{toast.title}</p>
                {toast.description && <p className="mt-1 text-xs leading-relaxed text-current/80" data-id="element-22">
                    {toast.description}
                  </p>}
              </div>
              <button type="button" onClick={() => dismissToast(toast.id)} className="rounded-md p-1 text-current/60 transition-colors hover:bg-black/5 hover:text-current" aria-label="Dismiss notification" data-id="element-23">
                <X className="h-4 w-4" data-id="element-24" />
              </button>
            </div>
          </div>;
      })}
      </div>
    </ToastContext.Provider>;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
export function InlineEmptyState({
  icon: Icon = AlertCircle,
  title,
  description,
  action
}: {
  icon?: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-6 py-10 text-center" data-id="element-25">
      <div className="mb-4 rounded-full bg-white p-3 shadow-sm" data-id="element-26">
        <Icon className="h-6 w-6 text-slate-400" data-id="element-27" />
      </div>
      <h3 className="text-base font-semibold text-foreground" data-id="element-28">{title}</h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground" data-id="element-29">
        {description}
      </p>
      {action && <div className="mt-5" data-id="element-30">{action}</div>}
    </div>;
}